import {announce} from '@github-ui/aria-live'
import {testIdProps} from '@github-ui/test-id-props'
import {Box, Button, FormControl, Select, TextInput, useOnOutsideClick} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import type {FormEvent, KeyboardEvent} from 'react'
import {forwardRef, useCallback, useMemo, useRef, useState} from 'react'

import type {SettingsOption} from '../../../helpers/new-column'
import {isPlatformMeta} from '../../../helpers/util'
import {DragAndDropResources} from '../../../strings'
import {AutocompleteSearch} from '../../common/autocomplete-search'
import {SingleSelectToken} from './single-select-token'

const formStyles: BetterSystemStyleObject = {display: 'flex', flexDirection: 'column', overflow: 'hidden'}
const dropdownItems = ['Move to row', 'Move item before', 'Move item after']

const dialogBodyStyles: BetterSystemStyleObject = {
  display: 'flex',
  gap: 3,
  overflowY: 'auto',
}

interface ReorderSingleSelectItemsModalProps {
  title: SettingsOption
  currentPosition: number
  onSave: (currentPosition: number, newPosition: number, before: boolean) => void
  onCancel: () => void
  options: Array<SettingsOption>
}

export const ReorderSingleSelectItemsModal = ({
  title,
  currentPosition,
  onSave,
  onCancel,
  options,
}: ReorderSingleSelectItemsModalProps) => {
  const [currentIndex, setIndex] = useState<number>(currentPosition)
  const [dropdownSelection, setDropdownSelection] = useState(dropdownItems[0])

  const containerRef = useRef<HTMLDivElement>(null)

  const onSubmit = (ev: FormEvent) => {
    ev.preventDefault() // prevent page reload
    switch (dropdownSelection) {
      case 'Move to row':
        if (currentIndex >= currentPosition) onSave(currentPosition, currentIndex, false)
        else onSave(currentPosition, currentIndex, true)
        break
      case 'Move item before':
        onSave(currentPosition, currentIndex, true)
        break
      case 'Move item after':
        onSave(currentPosition, currentIndex, false)
        break
      default:
        onSave(currentPosition, currentIndex, false)
        break
    }
    announce(DragAndDropResources.successfulMove(title.name))
  }

  /** Submit on cmd/ctrl enter. */
  const onKeyDown = (ev: KeyboardEvent<HTMLFormElement>) => {
    if (ev.key === 'Enter' && isPlatformMeta(ev)) onSubmit(ev)
  }

  // Dialog doesn't use Overlay or call `useOnOutsideClick` under the hood. This means that
  // it doesn't get registered as an overlay in the stack, which means it doesn't intercept
  // and stop propagating clicks. This means that if there are any open `Overlay`s underneath
  // this `Dialog`, clicks in this dialog will register as clicks outside of those overlays,
  // causing them to try to close.
  // We can prevent this by registering a fake `useOnClickOutside` hook to make this look like
  // a regular `Overlay`. Clicks inside will no-op and the hook will prevent them from propagating
  // to other overlays; clicks outside need to have `preventDefault` called on them to stop them
  // from propagating.
  useOnOutsideClick({containerRef, onClickOutside: e => e.preventDefault()})

  return (
    <Dialog
      title={
        <Box as="span" sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          Move <SingleSelectToken option={title} />
        </Box>
      }
      onClose={onCancel}
      width="medium"
      ref={containerRef}
      aria-label={`Move ${title.name} dialog`}
      renderBody={() => (
        // By default there is no way to wrap the footer and body in one element, so we
        // have to custom-render the footer inside the body. Otherwise we can't use `submit`
        // button to submit the form
        <Box
          as="form"
          onSubmit={onSubmit}
          onKeyDown={onKeyDown}
          // Stopping blur propagation fixes a bug where the table thinks the cell editor was
          // blurred when it was really an input inside this dialog.
          onBlur={e => e.stopPropagation()}
          sx={formStyles}
        >
          <Dialog.Body sx={dialogBodyStyles}>
            <MoveInput
              options={options}
              onChange={index => setIndex(index)}
              initialPosition={currentPosition + 1}
              dropdownSelection={dropdownSelection}
              setDropdownSelection={setDropdownSelection}
            />
          </Dialog.Body>

          <Dialog.Footer sx={{padding: 2}}>
            <Button onClick={onCancel} sx={{marginRight: 1}}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Save
            </Button>
          </Dialog.Footer>
        </Box>
      )}
    />
  )
}

interface InputProps {
  options: Array<SettingsOption>
  dropdownSelection: string | undefined
  onChange: (value: number) => void
  setDropdownSelection: (selection: string) => void
  initialPosition: number
}

const MoveInput = forwardRef<HTMLInputElement, InputProps>(function NameInput(
  {onChange, setDropdownSelection, dropdownSelection, options, initialPosition},
  ref,
) {
  const autocompleteTemplateList: Array<{text: string; id: string}> = useMemo(() => {
    if (options.length > 0) {
      return options.map((option, index) => {
        return {
          text: option.name,
          id: `${index + 1}`,
        }
      })
    }
    return []
  }, [options])
  const [selectedAutocomplete, setSelectedAutocomplete] = useState<{text: string; id: string}>({
    text: '',
    id: '0',
  })

  const autocompleteSelection = useCallback(
    (selectedItem: {text: string; id: string}) => {
      setSelectedAutocomplete(selectedItem)
      onChange(parseInt(selectedItem.id, 10) - 1)
    },
    [onChange],
  )

  const rowInputParse = (value: string) => {
    const parsedValue = parseInt(value)
    onChange(parsedValue - 1)
  }

  return (
    <Box sx={{display: 'flex', width: '100%', flexDirection: 'column', gap: 3}}>
      <FormControl required>
        <FormControl.Label>Direction</FormControl.Label>
        <Select block onChange={e => setDropdownSelection(e.currentTarget.value)}>
          {dropdownItems.map(item => (
            <Select.Option key={item} value={item}>
              {item}
            </Select.Option>
          ))}
        </Select>
      </FormControl>
      {dropdownSelection === 'Move to row' ? (
        <FormControl required>
          <FormControl.Label>Position</FormControl.Label>
          <TextInput
            block
            min={1}
            max={options.length}
            defaultValue={initialPosition}
            type="number"
            {...testIdProps('single-select-move-text-input')}
            onChange={ev => rowInputParse(ev.target.value)}
            ref={ref}
            aria-label={`${dropdownSelection} input`}
          />
        </FormControl>
      ) : (
        <AutocompleteSearch
          items={autocompleteTemplateList}
          label="Position"
          hideLabel={false}
          required
          sx={{padding: '0', margin: '0'}}
          onSelect={selectedItem => autocompleteSelection(selectedItem)}
          selectedItem={selectedAutocomplete}
          aria-label={`${dropdownSelection} input`}
        />
      )}
    </Box>
  )
})

try{ ReorderSingleSelectItemsModal.displayName ||= 'ReorderSingleSelectItemsModal' } catch {}
try{ MoveInput.displayName ||= 'MoveInput' } catch {}